import { Navigate, Outlet, useLocation } from "react-router-dom";
import { usePermissions } from "../helper/PermissionContext";

// Route path (matched by first path segment) -> permission slug required to
// open it. Add/edit sub-routes inherit their parent module's permission.
const ROUTE_PERMISSIONS = {
  "/": "dashboard",
  "/author": "blog-management",
  "/category": "blog-management",
  "/tag": "blog-management",
  "/blog": "blog-management",
  "/add-blog": "blog-management",
  "/edit-blog": "blog-management",
  "/news": "media-management",
  "/add-news": "media-management",
  "/edit-news": "media-management",
  "/news-category": "media-management",
  "/comparison-page": "comparison-page",
  "/add-comparison": "comparison-page",
  "/edit-comparison": "comparison-page",
  "/tms-integration-category": "tms-integration",
  "/tms-integration": "tms-integration",
  "/add-tms-integration": "tms-integration",
  "/edit-tms-integration": "tms-integration",
  "/contact": "contact-us",
  "/seo": "global-seo",
  "/add-seo": "global-seo",
  "/edit-seo": "global-seo",
  "/global-seo": "global-seo",
  "/schema-management": "global-seo",
  "/sitemap": "sitemap",
  "/llms": "llms-management",
  "/robots": "robots-management",
  "/newsletter": "newsletter",
  "/email-management": "email-management",
  "/add-email-management": "email-management",
  "/edit-email-management": "email-management",
  "/users": "user-management",
  "/roles": "user-management",
  "/permissions": "user-management",
  "/home-contact": "contact-us",
  "/stats": "home-stats",
  "/calculator": "calculator",
  "/add-calculator": "calculator",
  "/edit-calculator": "calculator",
  "/industry": "case-study",
  "/case-study": "case-study",
  "/add-case-study": "case-study",
  "/edit-case-study": "case-study",
  "/logistic-category": "logistic-service",
  "/logistic": "logistic-service",
  "/add-logistics": "logistic-service",
  "/edit-logistics": "logistic-service",
  "/feature-category": "feature-service",
  "/add-feature": "feature-service",
  "/feature-service": "feature-service",
  "/edit-feature": "feature-service",
  "/price": "pricing",
  "/add-price": "pricing",
  "/edit-price": "pricing",
  "/client-logos": "client-logos",
  "/add-client-logo": "client-logos",
  "/edit-client-logo": "client-logos",
  "/dynamic": "dynamic-page",
  "/add-dynamic": "dynamic-page",
  "/edit-dynamic": "dynamic-page",
  "/faq": "faq-management",
  "/add-faq": "faq-management",
  "/edit-faq": "faq-management",
};

// Match by path segments so "/logistic" never matches "/logistic-category".
const permissionForPath = (pathname) => {
  const segments = pathname.split("/").filter(Boolean);
  if (segments.length === 0) return ROUTE_PERMISSIONS["/"];
  for (const [key, slug] of Object.entries(ROUTE_PERMISSIONS)) {
    if (key === "/") continue;
    const keySegments = key.split("/").filter(Boolean);
    if (keySegments.every((segment, index) => segments[index] === segment)) return slug;
  }
  return null; // unknown route -> no restriction
};

const AccessDenied = () => (
  <div
    style={{
      padding: "60px 20px",
      textAlign: "center",
      fontFamily: "inherit",
    }}
  >
    <h3 style={{ margin: "0 0 10px", fontSize: "22px" }}>Access Denied</h3>
    <p style={{ margin: 0, opacity: 0.7 }}>You do not have permission to view this page.</p>
  </div>
);

// Guards the <Outlet />: blocks routes the current user has no permission for.
const RoutePermissionGuard = () => {
  const { pathname } = useLocation();
  const { status, hasPermission } = usePermissions();
  const required = permissionForPath(pathname);

  if (status === "loading") {
    return <div>Loading...</div>;
  }

  if (required && !hasPermission(required)) {
    // Redirect to the dashboard when the user is allowed there, otherwise show
    // an access denied message (avoids redirect loops for users with no dashboard access).
    return hasPermission("dashboard") ? <Navigate to="/" replace /> : <AccessDenied />;
  }

  return <Outlet />;
};

export default RoutePermissionGuard;
